import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import { v4 } from 'uuid';

import { isFormBasedTrigger } from '@xmatters/common/constants/scriptSteps';
import { formTypes } from 'xm-constants/forms';
import {
  deserializeCreateAlertResponseOptions,
  deserializeCreateAlertTriggers,
} from 'utils/flowBlockInput';
import { alertTriggerTypeValues } from 'xm-constants/triggerTypes';
import { CreateAlertStepInputNames } from 'xm-constants/toolStep';
import { EMPTY_ARRAY } from 'utils/immutable';
import { DEFAULT_SOURCE_PORT_ID } from 'utils/defaultCanvasPortId';
import { buildDynamicOutputsByBlockIdMap } from './utils/buildDynamicOutputsByBlockIdMap';

const getFlows = state => state.flowDesigner.flows;
export const getFlowsById = createSelector([getFlows], flows =>
  keyBy(flows, 'canvasId'),
);
export const getFlowNamesSet = createSelector(
  [getFlows],
  flows => new Set(map(flows, 'canvasName')),
);

export const getPlanId = state => state.flowDesigner.planId;
export const getIsCanvasDirty = state => state.flowDesigner.isCanvasDirty;
export const getIntegrations = state =>
  get(state, 'flowDesigner.uiIntegrations', EMPTY_ARRAY);
export const getIntegrationsById = createSelector(
  [getIntegrations],
  integrations => keyBy(integrations, 'id'),
);
export const getApiIntegrations = state =>
  get(state, 'flowDesigner.apiIntegrations');
export const getApiIntegrationsById = createSelector(
  [getApiIntegrations],
  integrations => keyBy(integrations, 'id'),
);
export const getCanvasId = state => get(state, 'flowDesigner.canvasId');
export const getAllCanvases = state => get(state, 'flowDesigner.allCanvases');

export const getCanvas = state => state.flowDesigner.planCanvas;

export const getCanvasForm = state =>
  get(state, 'flowDesigner.planCanvas.form');

export const getForms = state => state.flowDesigner.forms;
export const getFormsById = createSelector([getForms], forms =>
  keyBy(forms, 'id'),
);

export const getApplicationId = state =>
  state.currentCommunicationPlan.applicationId;

export const getFlowForms = state =>
  filter(state.flowDesigner.forms, form => form.triggerType === formTypes.FLOW);

export const getMessagingForms = createSelector([getForms], forms =>
  filter(forms, form => form.triggerType === formTypes.MESSAGING),
);

export const getIntegrationById = (state, props) => {
  const { integrationId } = props;
  const allIntegrations = getIntegrations(state);
  return allIntegrations[integrationId];
};

export const getExistingFormBasedTriggerIntegrations = createSelector(
  [getIntegrations],
  integrations =>
    integrations.filter(
      integration =>
        isFormBasedTrigger(integration.type) &&
        integration.form &&
        integration.form.id,
    ),
);
export const getCanvasesById = createSelector([getAllCanvases], canvases =>
  keyBy(canvases, 'id'),
);
export const getCurrentCanvas = createSelector(
  [getCanvasesById, getCanvasId],
  (canvases, canvasId) => canvases && canvases[canvasId],
);

export const getBlocks = ({ flowDesigner }) => flowDesigner.blocks;
export const getPlanCanvasBlocks = state =>
  get(state, 'flowDesigner.planCanvas.blocks', EMPTY_ARRAY);

export const getBlockByIdMap = createSelector([getPlanCanvasBlocks], blocks =>
  Object.freeze(keyBy(blocks, 'id')),
);

const getGroupingBlockId = (state, groupingBlockId) => groupingBlockId;
export const getBlocksByGroupingBlockId = createSelector(
  [getBlocks, getGroupingBlockId],
  (blocks, groupingBlockId) =>
    blocks.filter(block => block.groupingBlockId === groupingBlockId),
);

export const getBlockByStepIdMap = createSelector(
  [getPlanCanvasBlocks],
  blocks => Object.freeze(keyBy(blocks, 'step.id')),
);

export const getHasGeneratedForLegacyIntegrationBlocks = createSelector(
  [getPlanCanvasBlocks],
  (blocks = []) => blocks.some(block => block.isGeneratedForLegacyIntegration),
);

export const getEdges = ({ flowDesigner }) => flowDesigner.planCanvas?.edges;
/**
 * `edge.from.block.id` seems to already be of format `$BLOCK_ID.$PORT_ID`,
 *    e.g.: `74ed37c2-fae1-4d6b-94a5-59b25e8db417.defaultSourcePort`
 * This was likely done to placate JSPlumb...
 */
export const getEdgesByFromBlockAndPortId = createSelector([getEdges], edges =>
  Object.freeze(keyBy(edges, 'from.block.id')),
);

export const getResponses = ({ flowDesigner }) => flowDesigner.responses;

export const getSavedCanvasIntegrations = createSelector(
  [
    getApiIntegrations,
    getAllCanvases,
    getCanvasId,
    getBlockByStepIdMap,
    getPlanCanvasBlocks,
    getResponses,
  ],
  (
    integrations,
    allCanvases,
    canvasId,
    blockStepIdMap,
    legacyInboundIntegrationEnabled,
    blocks,
    responses,
  ) => {
    const canvas = find(allCanvases, c => c.id === canvasId);
    if (!canvas) {
      return [];
    }

    const responsesTriggerOnCanvas = !isNil(
      find(blocks, b => b?.step?.type === 'RESPONSES'),
    );
    return filter(
      integrations,
      i =>
        canvas.plan.id === i.plan.id &&
        (blockStepIdMap[i.id] ||
          (responsesTriggerOnCanvas &&
            i.triggeredBy === 'RESPONSE' &&
            find(responses, r => r.id === i.responseOption?.id))) &&
        // TODO remove filter when COR-15608 feature flag is removed
        (legacyInboundIntegrationEnabled ||
          !(i.integrationType === 'INBOUND_WEBHOOK' && i.createdBy === 'IB')),
    );
  },
);

export const getFlowDesignerIntegrationSteps = state =>
  state.flowDesigner.integrationSteps;
export const getFlowDesignerCustomSteps = state =>
  state.flowDesigner.customSteps;

export const getFlowDesignerCustomStepsById = createSelector(
  [getFlowDesignerCustomSteps],
  customSteps => keyBy(customSteps, 'id'),
);

// Deprecated: use planCanvas.inputs instead
export const getInputs = ({ flowDesigner }) => flowDesigner.inputs;

export const getPlanCanvasInputs = ({ flowDesigner }) =>
  flowDesigner.planCanvas?.inputs;

export const getInputByBlockIdMap = createSelector([getInputs], (inputs = []) =>
  keyBy(inputs, ({ flowBlock }) => flowBlock.id),
);

const getProperty = (state, blockId) =>
  get(getInputByBlockIdMap(state), [blockId, 'value']);

// Removes the `${}`:
const stripTemplateWrapper = str => {
  const wrapped = str && /\$\{(.+)\}/gi.exec(str);
  return wrapped && wrapped[1];
};

export const getStrippedSwitchProperty = (state, blockId) =>
  stripTemplateWrapper(getProperty(state, blockId));

// Create Alert section
const getBlockId = (state, { blockId }) => blockId;

function makeBuiltinInputFindSelector(inputName) {
  return createSelector(
    [getPlanCanvasInputs, getBlockId],
    (inputs, flowBlockId) =>
      inputs?.find(
        input =>
          input.flowBlock.id === flowBlockId &&
          input?.scriptStepInput?.name === inputName,
      ),
  );
}

const getCreateAlertResponsesInput = makeBuiltinInputFindSelector(
  CreateAlertStepInputNames.RESPONSES,
);
export const getCreateAlertResponsesInputId = createSelector(
  [getCreateAlertResponsesInput],
  responsesInput => responsesInput?.scriptStepInput?.id,
);
export const getCreateAlertResponsesInputValue = createSelector(
  [getCreateAlertResponsesInput],
  responsesInput =>
    deserializeCreateAlertResponseOptions(responsesInput?.value),
);

const getCreateAlertTriggersInput = makeBuiltinInputFindSelector(
  CreateAlertStepInputNames.TRIGGERS,
);
export const getCreateAlertTriggersInputId = createSelector(
  [getCreateAlertTriggersInput],
  triggersInput => triggersInput?.scriptStepInput?.id,
);
export const getCreateAlertTriggersInputValue = createSelector(
  [getCreateAlertTriggersInput],
  triggersInput => deserializeCreateAlertTriggers(triggersInput?.value),
);

/**
 * Given the list of all possible triggers, and the ones that are already in use,
 * figure out what triggers are left to use.
 *
 * For example, if a list of incident status (MINIMUM, LOW, ... CRITICAL) can be used
 * as triggers for the Initiate Incident block, and right now we have MINIMUM & LOW
 * active on canvas right now, this function will give us the trigger objects for
 * MEDIUM, HIGH & CRITICAL in an array.
 *
 * @param {string[]} allTriggers - The list of all possible triggers. For example, SEVERITY_LIST
 * @param {object} activeTriggers - The list of active triggers on the canvas.
 * @returns {object[]} The lit of available triggers left to use.
 */
export function getAvailableTriggers(allTriggers, activeTriggers) {
  if (!activeTriggers) return [];

  return filter(allTriggers, triggerType => {
    const trigger = activeTriggers[triggerType];
    return !trigger || trigger.hidden || trigger.position == null;
  }).map(triggerType => {
    const prevData = activeTriggers[triggerType] || {};

    return {
      triggerType,
      triggerBlockId: prevData.triggerBlockId || v4(),
      ...prevData,
    };
  });
}

export const getCreateAlertAvailableTriggers = createSelector(
  [getCreateAlertTriggersInputValue],
  triggers => getAvailableTriggers(alertTriggerTypeValues, triggers),
);

// Notify Step section end
export const allInitiateIncidentTriggers = state =>
  state.flowDesigner.planCanvas?.incidentTriggers ?? {};

export const getInitiateIncidentTriggers = createSelector(
  allInitiateIncidentTriggers,
  getBlockId,
  (triggers, blockId) => (triggers ? triggers[blockId] : {}),
);

export const getBlockBeingDraggedData = state =>
  state.flowDesignerDragToConnect.dragItemData;

export const getIsDraggingBlock = state => !!getBlockBeingDraggedData(state);

export const getDragToConnectTarget = state =>
  state.flowDesignerDragToConnect.dropTarget;

export const getEdgeConnectHighlight = state =>
  state.flowDesigner.edgeConnectHighlight;

export const getEdgeConnectHighlightedHandleId = (
  edgeConnectHighlight,
  nodeId,
) => {
  if (
    nodeId === edgeConnectHighlight?.sourceHandleId ||
    (nodeId === edgeConnectHighlight?.sourceNodeId &&
      edgeConnectHighlight?.sourceHandleId === DEFAULT_SOURCE_PORT_ID)
  ) {
    return edgeConnectHighlight?.sourceHandleId;
  }
  if (nodeId === edgeConnectHighlight?.targetNodeId) {
    return edgeConnectHighlight?.targetHandleId;
  }

  return null;
};

// "Dynamic Outputs" really means "dynamic output definitions" A.K.A "Dynamic output mappings".
// (!) First retrieve each "I N P U T" that is named "Output Mappings",
// then parse their 'value' to create a map of dynamic output definitions organized by flowBlockId
// only including the outputs that have a "name" and "dataOriginPointer" set
export const getDynamicOutputsByBlockIdMap = createSelector(
  [getPlanCanvasInputs],
  planCanvasInputs => buildDynamicOutputsByBlockIdMap(planCanvasInputs),
);
